import {
  Card,
  CbhIcon,
  List,
  ListItem,
  ListItemText,
  Text,
  TextSkeleton,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Skeleton, Stack } from "@mui/material";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";

import { useGetQualificationsForRequirement } from "../../useGetQualificationsForRequirement";
import { getApplicableWorkplacesDetails } from "../../utils/getApplicableWorkplacesDetails";
import { getApplicableWorkplacesDisplayTextV2 } from "../../utils/getApplicableWorkplacesDisplayText";
import { getRequirementWorkplacesIds } from "../getRequirementWorkplacesIds";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";

/**
 * Shows which qualifications and workplaces a document requirement applies to.
 * Displays a card with qualification labels and facility information.
 *
 * Currently used in the TB Test guided upload page. Can be adapted for other document detail pages.
 *
 * Note: This component does not handle credential requirement filtering or workplace tap navigation.
 * These features would need to be ported from {@link DocumentDetailsRequiredBySection} if this
 * component is used more generally.
 */
export function DocumentDetailsApplicability() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const { qualifications } = workerRequirement;
  const worker = useDefinedWorker();

  const { truncatedQualifications, isLoading: isLoadingQualifications } =
    useGetQualificationsForRequirement(qualifications, {
      truncationPrefix: "",
    });

  const requiredWorkplaceIds = getRequirementWorkplacesIds(workerRequirement);
  const applicableWorkplacesDetails = getApplicableWorkplacesDetails({
    workerRequirement,
    workerAddress: worker.address,
    requiredByWorkplacesCount: requiredWorkplaceIds.length,
  });

  const singleWorkplaceId = requiredWorkplaceIds.length === 1 ? requiredWorkplaceIds[0] : undefined;

  const {
    data: workplace,
    isLoading: isLoadingWorkplace,
    fetchStatus,
  } = useGetWorkplace(
    { workplaceId: singleWorkplaceId },
    {
      enabled: isDefined(applicableWorkplacesDetails) && isDefined(singleWorkplaceId),
      staleTime: minutesToMilliseconds(5),
    }
  );

  const workplaceName = workplace?.data?.attributes?.name;
  const workplacesDisplayText = getApplicableWorkplacesDisplayTextV2({
    applicableWorkplacesDetails,
    isSingleWorkplace: isDefined(singleWorkplaceId) && isDefined(workplaceName),
    workplaceName,
  });

  const isLoading = isLoadingQualifications || (isLoadingWorkplace && fetchStatus !== "idle");

  if (isLoading) {
    return (
      <Stack spacing={5}>
        <TextSkeleton variant="body1" width="35%" />
        <Card variant="tertiary" sx={{ paddingY: 1 }}>
          <CardContent>
            <List disablePadding>
              <ListItem sx={{ paddingY: 1 }}>
                <Stack direction="row" spacing={3} alignItems="center" flex={1}>
                  <Skeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0 }} />
                  <ListItemText primary={<TextSkeleton variant="body1" width="50%" />} />
                </Stack>
              </ListItem>
              <ListItem sx={{ paddingY: 1 }}>
                <Stack direction="row" spacing={3} alignItems="center" flex={1}>
                  <Skeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0 }} />
                  <ListItemText primary={<TextSkeleton variant="body1" width="65%" />} />
                </Stack>
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  const hasQualifications = isDefined(truncatedQualifications);
  const hasWorkplaces = isDefined(workplacesDisplayText);

  if (!hasQualifications && !hasWorkplaces) {
    return null;
  }

  return (
    <Stack spacing={5}>
      <Text bold variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
        This applies to
      </Text>

      <Card variant="tertiary" sx={{ paddingY: 1 }}>
        <CardContent>
          <List disablePadding>
            {hasQualifications && (
              <ListItem sx={{ paddingY: 1 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <CbhIcon type="user" size="small" />
                  <ListItemText primary={<Text variant="body2">{truncatedQualifications}</Text>} />
                </Stack>
              </ListItem>
            )}
            {hasWorkplaces && (
              <ListItem sx={{ paddingY: 1 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <CbhIcon type="building" size="small" />
                  <ListItemText primary={<Text variant="body2">{workplacesDisplayText}</Text>} />
                </Stack>
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </Stack>
  );
}
